import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import FileDropArea from '@/components/common/file-drop-area.component.vue';               // 共通ファイルドロップエリアコンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue'; // 共通メッセージダイアログコンポーネント
import SystemConst from '@/shared/system-const';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import vueRouter from '@/router/index';                                                    // ルート情報取得
import vueFilePond from 'vue-filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import { FilePondFile } from 'filepond';
import { ErrorTableType } from '@/shared/type';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    showErrorMessageDialog: boolean;                             // エラーメッセージダイアログ表示切り替え
    fileParamSetting: string;                                    // ファイルエラーメッセージ切り替え
    fileErrorItems: ErrorTableType[];                            // バリデーションエラーのデータ表示内容
    validateValues: ValidateType;                                // 取得したファイルバリデーションの値
    passwordFileErrorMessage: string;                            // パスワード情報ファイルエラーメッセージ
    isPasswordFileValid: boolean;                                // パスワード情報ファイルバリデーション結果（正常: true 異常: false）
    addresseeFileErrorMessage: string;                           // 宛名ファイルエラーメッセージ
    isAddresseeFileValid: boolean;                               // 宛名ファイルバリデーション結果（正常: true 異常: false）
    templateFileErrorMessage: string;                            // 雛形ファイルエラーメッセージ
    isTemplateFileValid: boolean;                                // 雛形ファイルバリデーション結果（正常: true 異常: false）
};

// バリデーション用パラメータ
interface ValidateType {
    [key: string]: string;                                       // パラメータ値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * 年度更新（通知データ作成）：ファイル選択コンポーネント
 */
export default defineComponent({
    components: { FileDropArea, CommonMessageDialog, FilePond },
    setup(props, ctx: SetupContext) {
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            showErrorMessageDialog: false,
            fileParamSetting: '1',
            fileErrorItems: [],
            validateValues: {},
            passwordFileErrorMessage: '',
            isPasswordFileValid: false,
            addresseeFileErrorMessage: '',
            isAddresseeFileValid: false,
            templateFileErrorMessage: '',
            isTemplateFileValid: false
        });
        const stepper = <number>1;
        const commonDialogRef = ref<any>(null);                  // 共通ダイアログ
        const passwordPondRef = ref<any>(null);                  // パスワード情報ファイルアップロードエリア
        const addresseePondRef = ref<any>(null);                 // 宛名ファイルアップロードエリア
        const templatePondRef = ref<any>(null);                  // 雛形ファイルアップロードエリア
        const tableHeight = SystemConst.FORM_HEIGHT.NOTIFICATION_FILE;
        let errors: string[] = [];                               // エラーメッセージ保存用変数

        // パスワード情報ファイルのバリデーション
        const passwordFilesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('passwordFileMaxSize' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = passwordPondRef.value.getFiles();

                // ファイルが最低1つ登録されていることを確認
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['passwordFileMaxSize']),
                    state.validateValues['passwordFileExtensions']
                );
            }
        ];

        // 宛名ファイルのバリデーション
        const addresseeFilesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('addresseeFileMaxSize' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = addresseePondRef.value.getFiles();

                // ファイルが最低1つ登録されていることを確認
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['addresseeFileMaxSize']),
                    state.validateValues['addresseeFileExtensions']
                );
            }
        ];

        // 雛形ファイルのバリデーション
        const templateFilesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('templateFileMaxSize' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = templatePondRef.value.getFiles();

                // ファイルが最低1つ登録されていることを確認
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['templateFileMaxSize']),
                    state.validateValues['templateFileExtensions']
                );
            }
        ];

        /**
         * ファイルバリデーション
         * 
         * @param files 対象ファイル
         * @param size バリデーション値（ファイルサイズ）
         * @param ext バリデーション値（拡張子）
         * @returns 正常の場合true エラーの場合メッセージ文字列
         */
        function validateFiles(files: FilePondFile[], size: number, ext: string) {
            // ファイルサイズが超過または0バイトでないか
            for (const key in files) {
                if (files[key].fileSize > size) {
                    // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_MAX_SIZE, [size / 1024]);
                } else if (files[key].fileSize === 0) {
                    // ファイルサイズが0バイトの場合、エラーメッセージを返す
                    return MessageConst.E_FILE_NO_DATA;
                }
            }

            // 拡張子が登録対象か
            for (const key in files) {
                // 登録しようとするファイルの拡張子を得る
                const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                const fileExtension = parts[1];

                // m_parametersの拡張子一覧
                const validateExtension = ext.split(',');

                if (validateExtension.indexOf(fileExtension) === -1) {
                    // 比較した結果、拡張子一覧に含まれていないとき、エラーメッセージを返す
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_EXTENSION, [ext]);
                }
            }

            return true;
        }

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-key-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '通知データ作成');

            // ファイルバリデーションパラメータ取得関数呼び出し
            getFileValidation();
        }

        /**
         * ファイルバリデーションパラメータ取得
         */
        async function getFileValidation() {
            // バリデーション取得APIを呼び出す
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('notificationData/getParameters');

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonDialogRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // バリデーション取得APIの結果を state.validateValues へ設定する
            state.validateValues['passwordFileMaxSize'] = response.data.passwordFileMaxSize;
            state.validateValues['passwordFileExtensions'] = response.data.passwordFileExtensions;
            state.validateValues['addresseeFileMaxSize'] = response.data.addresseeFileMaxSize;
            state.validateValues['addresseeFileExtensions'] = response.data.addresseeFileExtensions;
            state.validateValues['templateFileMaxSize'] = response.data.templateFileMaxSize;
            state.validateValues['templateFileExtensions'] = response.data.templateFileExtensions;
        }

        /**
         * 次へボタン押下時に、確認ダイアログを表示
         */
        function uploadConfirmation() {
            // 各ファイルのバリデーションチェックを実施する
            updatePasswordFile();
            updateAddresseeFile();
            updateTemplateFile();
            // バリデーションチェックにいずれかのファイルに一つでもエラーがあるか判別
            if (!(state.isPasswordFileValid && state.isAddresseeFileValid && state.isTemplateFileValid)) {
                // いずれかのファイルにエラーがある場合
                // 画面上部にスクロールして、バリデーションのエラーを表示する
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            // 確認メッセージを設定する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['ファイルアップロード']);
            // 確認メッセージダイアログ表示
            commonDialogRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 通知データ作成ファイルアップロード
         */
        async function uploadNotificationFiles() {
            // 通知データ作成に使用する3ファイルを取得し、
            // APIへ渡すためにFilePondFileオブジェクトからFileオブジェクトを作る
            const passwordFile: File[]
                = passwordPondRef.value.getFiles().map((e: FilePondFile) => e.file);
            const addresseeFile: File[]
                = addresseePondRef.value.getFiles().map((e: FilePondFile) => e.file);
            const templateFile: File[]
                = templatePondRef.value.getFiles().map((e: FilePondFile) => e.file);

            // ファイル内容登録処理を行うAPIを呼びだす
            const response = await ctx.root.$store.dispatch('notificationData/notificationFileUpload',
                {
                    passwordFile: passwordFile,
                    addresseeFile: addresseeFile,
                    templateFile: templateFile
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // バリデーションチェックでエラーが発生した場合（400のステータスコード）
                if (response.status === HttpStatus.BAD_REQUEST) {
                    // ローダーの表示を閉じる
                    commonDialogRef.value.state.showDialog = false;
                    // レスポンスのエラー内容を保存する
                    errors = response.data.errors;
                    // エラーダイアログ表示切替関数呼び出し
                    changeDialog();
                    return;
                } else {
                    // 400以外のステータスコードのエラーの場合
                    // エラー画面へ遷移
                    commonDialogRef.value.errorDisplayTransitionControl(response);
                    return;
                }
            }

            // チェック結果確認画面へ遷移する
            vueRouter.push('/fiscal-year-update/notification-data/result');
        }

        /**
         * パスワード情報ファイルが追加・削除された時のイベント
         */
        function updatePasswordFile() {
            // passwordFilesRules でバリデーションをチェックする
            for (let index = 0; index < passwordFilesRules.length; index++) {
                const rule: any = passwordFilesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーありの場合
                    // エラーメッセージ表示
                    state.passwordFileErrorMessage = valid;
                    state.isPasswordFileValid = false;
                    break;
                } else {
                    // エラーなしの場合
                    state.passwordFileErrorMessage = '';
                    state.isPasswordFileValid = true;
                }
            }
        }

        /**
         * 宛名ファイルが追加・削除された時のイベント
         */
        function updateAddresseeFile() {
            // addresseeFilesRules でバリデーションをチェックする
            for (let index = 0; index < addresseeFilesRules.length; index++) {
                const rule: any = addresseeFilesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーありの場合
                    // エラーメッセージ表示
                    state.addresseeFileErrorMessage = valid;
                    state.isAddresseeFileValid = false;
                    break;
                } else {
                    // エラーなしの場合
                    state.addresseeFileErrorMessage = '';
                    state.isAddresseeFileValid = true;
                }
            }
        }

        /**
         * 雛形ファイルが追加・削除された時のイベント
         */
        function updateTemplateFile() {
            // templateFilesRules でバリデーションをチェックする
            for (let index = 0; index < templateFilesRules.length; index++) {
                const rule: any = templateFilesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーありの場合
                    // エラーメッセージ表示
                    state.templateFileErrorMessage = valid;
                    state.isTemplateFileValid = false;
                    break;
                } else {
                    // エラーなしの場合
                    state.templateFileErrorMessage = '';
                    state.isTemplateFileValid = true;
                }
            }
        }

        /**
         * エラーダイアログ表示切替
         */
        function changeDialog(fileParam = '1') {
            state.fileParamSetting = fileParam;
            // エラー表示内容を初期化する
            state.fileErrorItems = [];
            let index = 0;
            // エラーレスポンスデータの件数分繰り返す
            for (let i = 0; i < errors.length; i++) {
                // エラーメッセージ一件取得して「、」で分割する
                const oneError = errors[i].split('、');
                // state.fileParamSetting が'1'（パスワード情報ファイル）で、分割した最初のデータが「パスワード」の場合
                if (state.fileParamSetting === '1' && /^パスワード$/.test(oneError[0])) {
                    // 「、」で分割した真ん中のデータをstate.fileErrorItems の'errorLocation'に設定する
                    // 「、」で分割した後ろのデータをstate.fileErrorItems の'message'に設定する
                    state.fileErrorItems[index] = { errorLocation: oneError[1], message: oneError[2] };
                    index++;

                } else if (state.fileParamSetting === '2' && /^宛名$/.test(oneError[0])) {
                    // state.fileParamSetting が'2'（宛名ファイル）で、分割した最初のデータが「宛名」の場合
                    // 「、」で分割した真ん中のデータをstate.fileErrorItems の'errorLocation'に設定する
                    // 「、」で分割した後ろのデータをstate.fileErrorItems の'message'に設定する
                    state.fileErrorItems[index] = { errorLocation: oneError[1], message: oneError[2] };
                    index++;

                } else if (state.fileParamSetting === '3' && /^雛形$/.test(oneError[0])) {
                    // state.fileParamSetting が'3'（雛形ファイル）で、分割した最初のデータが「雛形」の場合
                    // 「、」で分割した真ん中のデータをstate.fileErrorItems の'errorLocation'に設定する
                    // 「、」で分割した後ろのデータをstate.fileErrorItems の'message'に設定する
                    state.fileErrorItems[index] = { errorLocation: oneError[1], message: oneError[2] };
                    index++;
                }
            }
            // エラーのない表示が選択された場合
            if (state.fileErrorItems.length === 0) {
                state.fileErrorItems[index] = {errorLocation: '', message: MessageConst.S_NO_ERROR_MESSAGE};
            }

            // エラーダイアログを表示する
            state.showErrorMessageDialog = true;
            return;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            stepper,
            commonDialogRef,
            passwordPondRef,
            addresseePondRef,
            templatePondRef,
            tableHeight,
            onResize,
            uploadConfirmation,
            uploadNotificationFiles,
            updatePasswordFile,
            updateAddresseeFile,
            updateTemplateFile,
            changeDialog
        };
    }
});
